'use client'

import * as React from 'react'
import Link from 'next/link'
import { ArrowLeft, Home, LogIn, RotateCw, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useShellTranslation } from '@/lib/shell/use-shell-translation'
import {
  STATUS_ARTWORK_SIZE,
  statusArtworkSrc,
  statusCode,
  statusCopyKey,
  statusHomeHref,
  type StatusKind,
} from '@/lib/status/status-artwork'
import { cn } from '@/lib/utils'

/**
 * The page a person lands on when something stands between them and what they
 * asked for: signed out (401), a page outside their role (403), a page that
 * does not exist (404), a failure on our side (500), maintenance (503), no
 * connection, a module that is not switched on, an expired link, or too many
 * requests (429).
 *
 * One illustration from the product's house style (the role and Vishing
 * artwork family — public/static/status/artwork, drawn by
 * apps/api/scripts/generate-status-artwork.ts), one heading, one or two
 * sentences and at most two actions. Every word comes from
 * `shell.status.<state>` unless the caller passes its own (the route gate keeps
 * its "This page isn't part of your role"); nothing a server or an exception
 * says is ever shown.
 *
 * The picture is DESCRIBED (alt from the catalogue) everywhere it appears: it
 * tells the state as much as the heading does. It is a plain <img> — the files
 * are already small WebPs, and the same element works on a signed-out page,
 * under the root error boundary and in a test — and it loads eagerly, because
 * it is always above the fold.
 */

export type StatusActionIcon = 'home' | 'back' | 'retry' | 'signIn'

export interface StatusAction {
  label: string
  /** A link (the home page, the sign-in page)… */
  href?: string
  /** …or a button (Try again, Go back). */
  onClick?: () => void
  icon?: StatusActionIcon
  /** A button that is busy (a request on its way). */
  disabled?: boolean
}

type DefaultAction = 'home' | 'back' | 'retry' | 'signIn'

/** What each state offers when the caller does not say. */
const DEFAULT_ACTIONS: Record<StatusKind, { primary: DefaultAction; secondary?: DefaultAction }> = {
  '401': { primary: 'signIn' },
  '403': { primary: 'home', secondary: 'back' },
  '404': { primary: 'home', secondary: 'back' },
  '500': { primary: 'retry', secondary: 'home' },
  '503': { primary: 'retry' },
  offline: { primary: 'retry' },
  'module-off': { primary: 'home', secondary: 'back' },
  expired: { primary: 'signIn' },
  '429': { primary: 'retry', secondary: 'home' },
}

const ICONS: Record<StatusActionIcon, LucideIcon> = {
  home: Home,
  back: ArrowLeft,
  retry: RotateCw,
  signIn: LogIn,
}

const NO_SUBSCRIBE = () => () => {}

/**
 * The address the panel is shown on, read from the browser rather than the
 * router: the panel also stands in for a page whose router context is gone
 * (an error boundary) and is rendered under many partial router mocks. The
 * server snapshot is null, so the first render and hydration agree.
 */
function useCurrentPath(): string | null {
  return React.useSyncExternalStore(
    NO_SUBSCRIBE,
    () => window.location.pathname,
    () => null,
  )
}

/** Back one page; a tab opened on this address has no history, so it goes home instead. */
export function goBackOrHome(home: string): void {
  if (typeof window === 'undefined') return
  if (window.history.length > 1) window.history.back()
  else window.location.assign(home)
}

export interface StatusPanelProps {
  kind: StatusKind
  /** Replaces the state's own heading (`shell.status.<state>.title`). */
  title?: React.ReactNode
  /** Replaces the state's own sentences (`shell.status.<state>.body`). */
  body?: React.ReactNode
  /** Replaces the default first action; `null` shows none. */
  primary?: StatusAction | null
  /** Replaces the default second action; `null` shows none. */
  secondary?: StatusAction | null
  /** What the default "Try again" does. Without it, the page reloads. */
  onRetry?: () => void
  /** Where the default "Back to Home" goes. Without it, the area's own front page. */
  homeHref?: string
  /** Where the default sign-in action goes. */
  signInHref?: string
  /** The heading's level: 1 when the panel IS the page, 2 inside a page that has its own. */
  headingLevel?: 1 | 2
  /** `page` fills the window; `section` sits inside a layout (beside the sidebar). */
  variant?: 'page' | 'section'
  /** Extra content between the sentences and the actions (a hint, a detail). */
  children?: React.ReactNode
  /** Small print under the actions (a support reference). */
  footer?: React.ReactNode
  className?: string
}

export function StatusPanel({
  kind,
  title,
  body,
  primary,
  secondary,
  onRetry,
  homeHref,
  signInHref = '/login',
  headingLevel = 1,
  variant = 'section',
  children,
  footer,
  className,
}: StatusPanelProps) {
  const { t } = useShellTranslation()
  const pathname = useCurrentPath()
  const home = homeHref ?? statusHomeHref(pathname)
  const code = statusCode(kind)

  const resolve = (which: DefaultAction, field: 'primary' | 'secondary'): StatusAction => {
    const label = t(statusCopyKey(kind, field))
    switch (which) {
      case 'home':
        return { label, href: home, icon: 'home' }
      case 'signIn':
        return { label, href: signInHref, icon: 'signIn' }
      case 'back':
        return { label, onClick: () => goBackOrHome(home), icon: 'back' }
      case 'retry':
        return { label, onClick: onRetry ?? (() => window.location.reload()), icon: 'retry' }
    }
  }

  const defaults = DEFAULT_ACTIONS[kind]
  const first = primary === undefined ? resolve(defaults.primary, 'primary') : primary
  const second =
    secondary === undefined ? (defaults.secondary ? resolve(defaults.secondary, 'secondary') : null) : secondary
  const actions = [first, second].filter((action): action is StatusAction => action !== null)

  const Heading = headingLevel === 1 ? 'h1' : 'h2'

  return (
    <div
      data-testid="status-panel"
      data-status-kind={kind}
      className={cn(
        'relative isolate flex w-full flex-col items-center justify-center px-4 text-center sm:px-6',
        variant === 'page' ? 'min-h-svh py-12 sm:py-16' : 'flex-1 py-10 sm:py-14',
        className,
      )}
    >
      {variant === 'page' ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[32rem] bg-gradient-to-b from-primary/[0.07] via-primary/[0.02] to-transparent dark:from-primary/[0.12]"
        />
      ) : null}

      <div className="flex w-full max-w-xl flex-col items-center">
        <StatusArtwork
          kind={kind}
          alt={t(statusCopyKey(kind, 'alt'))}
          className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-700 motion-safe:fill-mode-both"
        />

        <div className="mt-8 flex flex-col items-center gap-3 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-3 motion-safe:duration-700 motion-safe:delay-150 motion-safe:fill-mode-both">
          {code ? (
            <span
              data-testid="status-code"
              className="rounded-full border bg-card/80 px-3 py-1 font-mono text-xs font-medium text-muted-foreground"
            >
              {t('shell.status.code', { code })}
            </span>
          ) : null}
          <Heading className="text-balance text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {title ?? t(statusCopyKey(kind, 'title'))}
          </Heading>
          <p className="max-w-md text-pretty text-base leading-relaxed text-muted-foreground">
            {body ?? t(statusCopyKey(kind, 'body'))}
          </p>
        </div>

        {children ? <div className="mt-6 w-full max-w-md">{children}</div> : null}

        {actions.length > 0 ? (
          <div
            data-testid="status-actions"
            className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:justify-center motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-700 motion-safe:delay-300 motion-safe:fill-mode-both"
          >
            {actions.map((action, index) => (
              <StatusActionButton key={index} action={action} primary={index === 0} />
            ))}
          </div>
        ) : null}

        {footer ? <div className="mt-6 text-xs leading-relaxed text-muted-foreground">{footer}</div> : null}
      </div>
    </div>
  )
}

function StatusActionButton({ action, primary }: { action: StatusAction; primary: boolean }) {
  const Icon = action.icon ? ICONS[action.icon] : null
  // "Back" points the way the reader's eye travels back: left in English, right in Arabic.
  const icon = Icon ? <Icon aria-hidden="true" className={cn('size-4', action.icon === 'back' && 'rtl-flip')} /> : null
  const look = primary ? 'default' : 'outline'
  const width = 'w-full sm:w-auto sm:min-w-40'
  if (action.href && !action.disabled) {
    return (
      <Button asChild size="lg" variant={look} className={width}>
        <Link href={action.href}>
          {icon}
          {action.label}
        </Link>
      </Button>
    )
  }
  return (
    <Button
      type="button"
      size="lg"
      variant={look}
      className={width}
      onClick={action.onClick}
      disabled={action.disabled}
    >
      {icon}
      {action.label}
    </Button>
  )
}

/**
 * The state's picture in a soft frame: a pale mat with a hairline ring that
 * holds on a light page and a dark one alike, and the picture's own colours
 * blurred into a glow behind it. The illustration carries its own background,
 * so on a dark page it reads as a lit window rather than a glaring card.
 */
export function StatusArtwork({
  kind,
  alt,
  size = 'lg',
  className,
}: {
  kind: StatusKind
  alt: string
  size?: 'lg' | 'md' | 'sm'
  className?: string
}) {
  const src = statusArtworkSrc(kind)
  const width = size === 'lg' ? 'max-w-[26rem]' : size === 'md' ? 'max-w-[18rem]' : 'max-w-[11rem]'
  const round = size === 'sm' ? 'rounded-2xl' : 'rounded-[2rem]'
  const innerRound = size === 'sm' ? 'rounded-xl' : 'rounded-[1.5rem]'
  return (
    <div className={cn('relative isolate w-full', width, className)}>
      {/* eslint-disable-next-line @next/next/no-img-element -- see the component note: a plain, small, eager WebP */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        width={STATUS_ARTWORK_SIZE.width}
        height={STATUS_ARTWORK_SIZE.height}
        loading="eager"
        decoding="async"
        data-testid="status-artwork-glow"
        className={cn(
          'pointer-events-none absolute inset-0 -z-10 size-full scale-105 object-cover opacity-50 blur-3xl saturate-125 dark:opacity-30',
          round,
        )}
      />
      <div
        className={cn(
          'bg-card/80 p-1.5 shadow-[0_1px_2px_rgb(0_0_0/0.05),0_24px_48px_-20px_rgb(0_0_0/0.25)] ring-1 ring-black/5 sm:p-2 dark:bg-white/[0.04] dark:ring-white/10',
          round,
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- see the component note */}
        <img
          src={src}
          alt={alt}
          width={STATUS_ARTWORK_SIZE.width}
          height={STATUS_ARTWORK_SIZE.height}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          data-testid="status-artwork"
          className={cn('block aspect-[4/3] h-auto w-full object-cover dark:brightness-[0.92]', innerRound)}
        />
      </div>
    </div>
  )
}
